import { SaveWorkloadConfiguration, WorkloadClusterCommand } from './project-details.actions';
import { reconcileStorageStretch, getStorageStretchErrors } from 'pcs-domain';
import { hasInvalidClusterReuse } from '../../feature-modules/workload-configuration/workload-cluster-selection';
import { collectOrphanedClustersAndOwners, getSonDeletionMessage } from 'pcs-domain';
import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import { VCPM, PcsSizingEngineResponse, toComputeClusterConfiguration, syncReferencedHardwareConfiguration, buildNewClusterName, isWorkloadStretchedByInput, remapClonedDomainClusters } from 'pcs-domain';
import { Injectable, Injector, inject } from '@angular/core';
import { RefreshProjectCollaboration, ActivateNextTab, ActivateSelectedTab, FetchCustomerList, FetchProjectById, GetProductDetails, ResetState, SaveProject, UpdateProject, UpdateProjectMetadata, SetVcenterHostCount, GetProductOfferings, UpdateSideNav, ResetSideNav, UpdateWldSwComponents, UpdateDomainName, GetComponents, DeleteWorkloadDomain, CloneWorkloadDomain, UpdateManagementDomainSwComponents, GetWorkloadInputConfigiuration, GetDhciInputConfiguration, UpdateWldList, UpdateWorkloadReadyNodeRecommendation, UpdateManagementDomainReadyNodeRecommendation, UpdateManagementDomainRecommendation, UpdateManagementDomainConfiguration, UpdateWorkloadDomainRecommendation, GetHftRaidsInputConfigiuration, GetIOProfileInputConfigiuration, GetvSANOverheadsInputConfigiuration, DeActivateSubsequentTabs, ActivateSelectTabs, PPTReportDownload, SaveSummary, GetWorkloadTypeConfiguration, SaveSupercolliderEvent, LockProject, UnlockProject, SetSelectedRecommendationNode, SetOutputView, ClearProjectDetailsState, CreateHciCluster, UpdateHciClusterConfiguration, CreateComputeCluster, UpdateComputeClusterConfiguration, CreateStorageCluster, UpdateStorageClusterConfiguration, GarbageCollectOrphanedClusters, ResetHftRaidsAndIoProfileConfig, RecalculateClusterHardwareConfiguration, CommitOutputSelections } from './project-details.actions';
import { DashboardService } from '../../feature-modules/dashboard/dashboard.service';
import { catchError, EMPTY, Observable, of, tap, switchMap, throwError, throwIfEmpty } from 'rxjs';
import { WhitneyService } from '../../core/services/whitney.service';
import { PCSSizerSerive } from '../../core/services/pcsizer.service';
import { AppConstants } from '../../core/data-models/app-constants';
import { DOMAIN_TYPE, SUPERCOLLIDER_EVENT_TYPE } from '../../enums/pcsizer';
import { SideNavService } from '../../core/services/sidenav.service';
import { PCSProject } from '../data-models/project-state.model';
import { VCPMService } from '../../core/services/vcpm.service';
import { DataProvider } from 'pcs-domain';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { DataProviderConfigService } from '../../core/services/data-provider-config.service';
import { PCSStringUtils } from '../../core/utils/pcs-string-utils';
import { WorkloadDomainSizingService } from '../../feature-modules/workload-domain-sizing/workload-domain-sizing.service';
import { ReportClientService } from '../../core/services/report-client/report-client.service';
import { applyStagedChangeToProject } from '../../core/services/output-staged-changes.service';
import { VcfSizingOutput } from 'pcs-domain';
import { AuthState } from '../auth/auth.state';
import { AuthService } from '../../components/auth/auth.service';
import { ProjectMiddlewareService } from '../../core/services/project-middleware/project-middleware.service';
import { ProjectCollaborationService } from '../../core/services/project-collaboration.service';
import { RecommendationSummaryBuilderService } from '../../core/services/recommendation-summary-builder.service';

export interface PCSForm {
  model: undefined,
  dirty: false,
  status: '',
  errors: {}
}
export interface ProjectDetailsStateModel {
  projectId: string;
  currentProject: VCPM.Project | undefined;
  customerList: any[];
  sideNav: Array<NavTab>;
  wldComponents: DataProvider.Component[];
  workloadInputConfiguration: DataProvider.InputConfigurationResponse;
  dhciInputConfiguration?: DataProvider.InputConfigurationResponse;
  mgmtDomainInputConfiguration: any;
  mgntComponents: any;
  hftRaidsConfig: any;
  hftRaidsRequestContext?: { clusterType: 'HCI' | 'D_HCI'; hostFailuresToTolerate: number | undefined };
  ioProfileConfig: any;
  vsanOverheadsConfig: any;
  productOffering: VCPM.ProductOffering[];
  productConfig: any;
  workloadDomains: VCPM.WorkloadDomain[]; // TODO need to assess usability in UI
  managementDomain: VCPM.ManagementDomain;
  recommendation: {};
  assumptions: [];
  managementDomainComponents: PCSForm;
  vCenterHostCount: VcenterHostCountModal; // TODO if modal info is presentation, defer it to component
  pptReportExportProgress: number;
  pptExportBlob: Blob | undefined;
  summary: VcfSizingOutput.Summary | undefined;
  workloadTypeConfiguration: any;
  selectedRecommendationNode: string;
  // Output tab view toggle - always defaults to 'cluster' per requirement, independent of VCPM data.
  outputView: 'cluster' | 'workload';
  // Transient: the {id, name} of the entity a Create* cluster action just minted, read once by
  // the caller right after the dispatch resolves (mirrors pptExportBlob/vCenterHostCount's pattern
  // of using a transient state slot for a single async operation's result).
  lastCreatedClusterReference: VCPM.ClusterReference | null;
}

/**
 * Keeps the management domain cluster type in lock-step with the workload domains: stretched when
 * ANY workload (HCI, or the CON/SON side of a dHCI workload) is stretched, standard otherwise.
 * Mutates `projectData` in place; returns true when the management cluster type actually changed.
 */
function syncManagementClusterType(projectData: VCPM.ProjectData): boolean {
  const mgmtClusterConfiguration = projectData.managementDomain?.configuration?.clusterConfiguration;
  if (!mgmtClusterConfiguration) {
    return false;
  }
  const hasStretchedWorkload = (projectData.workloadDomains ?? []).some((workloadDomain: VCPM.WorkloadDomain) =>
    workloadDomain.workloadList?.some((workload: VCPM.Workload) => isWorkloadStretchedByInput(workload)),
  );
  const newClusterType = hasStretchedWorkload ? 'stretched' : 'standard';
  if (mgmtClusterConfiguration.clusterType === newClusterType) {
    return false;
  }
  mgmtClusterConfiguration.clusterType = newClusterType;
  return true;
}

@State<ProjectDetailsStateModel>({
  name: 'projectDetails',
  defaults: {
    projectId: '0',
    currentProject: undefined,
    customerList: [],
    sideNav: [{
      id: AppConstants.projectDetails,
      panel: 'project-details-panel',
      label: "Project Details",
      selected: true,
      disabled: false
    }],
    wldComponents: [],
    workloadInputConfiguration: {} as DataProvider.InputConfigurationResponse,
    mgmtDomainInputConfiguration: {},
    mgntComponents: {},
    hftRaidsConfig: {},
    ioProfileConfig: {},
    vsanOverheadsConfig: {},
    productConfig: {},
    productOffering: [],
    workloadDomains: [],
    managementDomain: {} as VCPM.ManagementDomain,
    recommendation: {

    },
    assumptions: [],
    managementDomainComponents: {
      model: undefined,
      dirty: false,
      status: '',
      errors: {}
    },
    vCenterHostCount: {
      hostCount: null,
      openModal: false
    },
    pptReportExportProgress: 0,
    pptExportBlob: undefined,
    summary: undefined,
    workloadTypeConfiguration: undefined,
    selectedRecommendationNode: 'summary',
    outputView: 'cluster',
    lastCreatedClusterReference: null
  },
})
@Injectable()
export class ProjectDetailsState {
  private readonly injector = inject(Injector);

  constructor(private dashboardService: DashboardService, private workloadDomainService: WorkloadDomainSizingService, private whitneyService: WhitneyService, private store: Store, private pcsizerService: PCSSizerSerive, private sideNavService: SideNavService, private vcpmService: VCPMService,
    private dataProviderConfigService: DataProviderConfigService, private reportService: ReportClientService, private projectMiddlewareService: ProjectMiddlewareService, private collaborationService: ProjectCollaborationService,
    private summaryBuilder: RecommendationSummaryBuilderService) {
  }

  /**
   * Persists `project` and then puts the engine-derived recommendation back onto the stored
   * currentProject.
   *
   * UpdateProject replaces currentProject wholesale with the backend's PUT response, and that
   * response does not carry the sizing engine's derived `profileRecommendations` candidates. Every
   * output save reads currentProject straight back out of the store, so without this the SECOND and
   * later saves in a session operated on - and re-persisted - a project whose tier cards had already
   * been stripped: showProfilePicker() is gated on that array, so the Small/Medium/Large + Selected
   * ReadyNode cards vanished, and computeSummary() fell back through
   * getEffectiveWorkloadRecommendation()'s `!profileRecommendations?.length` short-circuit to the
   * raw pre-tier sizing, silently reverting the saved summary.
   *
   * The response's own server-assigned fields (version, timestamps, lock/share state) are kept -
   * only `output.recommendation` and `output.summary`, which we just built and persisted ourselves,
   * are restored over it. Same reasoning RecommendationComponent already applies to its own local
   * copy; this gives the store the same treatment instead of leaving the two divergent.
   */
  private persistAndKeepEngineDerivedOutput(ctx: StateContext<ProjectDetailsStateModel>, project: VCPM.Project) {
    return this.store.dispatch(new UpdateProject(ctx.getState().projectId, project)).pipe(
      tap(() => {
        const stored = ctx.getState().currentProject;
        if (!stored?.data?.output || !project.data.output) {
          return;
        }
        const merged: VCPM.Project = JSON.parse(JSON.stringify(stored));
        merged.data.output!.recommendation = JSON.parse(JSON.stringify(project.data.output.recommendation));
        merged.data.output!.summary = project.data.output.summary;
        ctx.patchState({ currentProject: merged });
      }),
    );
  }

  // Rebuilding the summary is a best-effort refresh of a derived view (the left-nav "Clusters"
  // tree and Recommendation Summary page) - it must never abort the save itself (the actually-
  // important mutation) just because the recommendation happens to be missing a field the summary
  // builder assumes is present.
  private buildSummarySafely(project: VCPM.Project, productOffering: VCPM.ProductOffering[]): VcfSizingOutput.Summary | undefined {
    try {
      return this.summaryBuilder.buildSummary(project, productOffering);
    } catch (err) {
      console.error('Failed to rebuild recommendation summary after an output save', err);
      return undefined;
    }
  }



  @Selector()
  static getProductConfig(state: ProjectDetailsStateModel) {
    return state.productConfig;
  }

  @Selector()
  static getProductOffering(state: ProjectDetailsStateModel) {
    return state.productOffering;
  }

  @Selector()
  static getProductOfferingAndConfig(state: ProjectDetailsStateModel) {
    return { productOffering: state.productOffering, productConfig: state.productConfig };
  }

  @Selector()
  static getSideNav(state: ProjectDetailsStateModel) {
    return state.sideNav;
  }

  @Selector()
  static getSelectedRecommendationNode(state: ProjectDetailsStateModel) {
    return state.selectedRecommendationNode;
  }

  @Selector()
  static getOutputView(state: ProjectDetailsStateModel) {
    return state.outputView;
  }

  @Selector()
  static getWldComponents(state: ProjectDetailsStateModel) {
    return state.wldComponents;
  }

  @Selector()
  static getDhciInputConfig(state: ProjectDetailsStateModel) {
    return state.dhciInputConfiguration;
  }

  @Selector()
  static getWldInputConfig(state: ProjectDetailsStateModel) {
    return state.workloadInputConfiguration;
  }
  
  @Selector()
  static getMgmtDomainInputConfig(state: ProjectDetailsStateModel) {
    return state.mgmtDomainInputConfiguration;
  }

  @Selector()
  static getManagementDomainComponents(state: ProjectDetailsStateModel) {
    return state.mgntComponents;
  }

  @Selector()
  static getCurrentProject(state: ProjectDetailsStateModel) {
    return state.currentProject;
  }

  @Selector()
  static getCurrentProjectMetadata(state: ProjectDetailsStateModel) {
    return state.currentProject?.metadata;
  }

  @Selector()
  static getCurrentProjectProductSelected(state: ProjectDetailsStateModel) {
    return state.currentProject?.metadata.product;
  }

  @Selector()
  static getCurrentProjectProductVersion(state: ProjectDetailsStateModel) {
    return state.currentProject?.metadata.version;
  }

  @Selector()
  static getCurrentProjectId(state: ProjectDetailsStateModel) {
    return state.projectId;
  }

  @Selector()
  static getCustomerList(state: ProjectDetailsStateModel) {
    return state.customerList;
  }

  // @Selector()
  // static getVcenterHostCount(state: ProjectDetailsStateModel) {
  //   return state.vCenterHostCount;
  // }

  @Selector()
  static getHftRaidsConfig(state: ProjectDetailsStateModel) {
    return state.hftRaidsConfig;
  }

  @Selector()
  static getHftRaidsRequestContext(state: ProjectDetailsStateModel) {
    return state.hftRaidsRequestContext;
  }

  @Selector()
  static getIoProfileConfig(state: ProjectDetailsStateModel) {
    return state.ioProfileConfig;
  }

  @Selector()
  static getvSANOverheadsConfig(state: ProjectDetailsStateModel) {
    return state.vsanOverheadsConfig;
  }

  @Selector()
  static getWorkloadTypeConfig(state: ProjectDetailsStateModel) {
    return state.workloadTypeConfiguration;
  }

  @Selector()
  static getMgmtComponentsSuggestions(state: ProjectDetailsStateModel) {
    return state.currentProject?.data.output?.recommendation?.workloadDomainsRecommendation?.result.mgmtComponentsSuggestions;
  }

  @Selector()
  static getFileExportStatus(state: ProjectDetailsStateModel): number | string {
    return state.pptReportExportProgress;
  }

  @Selector()
  static getSavedSummary(state: ProjectDetailsStateModel): VcfSizingOutput.Summary | undefined {
    return state.summary;
  }

  @Selector()
  static getLastCreatedClusterReference(state: ProjectDetailsStateModel): VCPM.ClusterReference | null {
    return state.lastCreatedClusterReference;
  }

  // Combined, unfiltered HCI + dHCI-Compute "Use Existing Cluster" pool for one domain. Both HCI's
  // own picker and dHCI's Compute picker read this same selector; HCI's picker component filters
  // the result to `type === 'HCI'` itself (asymmetric pool rule - an HCI workload can never reuse
  // a ComputeCluster).
  static getReusableComputeClusters(domainId: number | undefined) {
    return createSelector([ProjectDetailsState], (state: ProjectDetailsStateModel): VCPM.ReusableComputeCluster[] =>
      state.currentProject?.data.workloadDomains?.find((d) => d.id === domainId)?.clusterConfigurations ?? []
    );
  }

  @Selector()
  static getReusableStorageClusters(state: ProjectDetailsStateModel): VCPM.StorageCluster[] {
    return state.currentProject?.data.clusterConfigurations ?? [];
  }


  // this action decides the side nav elements based on Architecture and Deployment for a project.
  @Action(UpdateSideNav)
  getSideNav(ctx: StateContext<ProjectDetailsStateModel>, action: UpdateSideNav) {
    let sideNavLabels = this.sideNavService.getSideNavTabs(action.architecture, action.deployment);
    ctx.patchState({ sideNav: sideNavLabels.tabs });
  }
  // resets the side nav to default state
  @Action(ResetSideNav)
  resetSideNav({patchState}: StateContext<ProjectDetailsStateModel>) {
    patchState({
      sideNav: this.sideNavService.getInitState()
    });
  }

  // not in use currently
  @Action(ActivateNextTab)
  activateNextTab(ctx: StateContext<ProjectDetailsStateModel>, action: ActivateNextTab) {
    let sideNav = ctx.getState().sideNav;
    let activeTabIndex = sideNav.findIndex(ele => ele.selected == true);
    if (activeTabIndex + 1 >= sideNav.length) {
      // Out of bounds - do nothing
    }
    else {
      for (let index = 0; index < sideNav.length; index++) {
        if (index == activeTabIndex + 1) {
          sideNav[index].selected = true;
        }
        else {
          sideNav[index].selected = false;
        }
      }
    }
    ctx.patchState({ sideNav: sideNav });
  }
  // sets next tab as active/selected based on response from API call
  @Action(ActivateSelectedTab)
  activateSelectedTab(ctx: StateContext<ProjectDetailsStateModel>, action: ActivateSelectedTab) {
    const sideNav = ctx.getState().sideNav.map(ele => {
      if (ele.id == action.tabId) {
        return { ...ele, selected: true, disabled: false };
      }
      return ele.selected ? { ...ele, selected: false } : ele;
    });
    ctx.patchState({ sideNav: sideNav });
  }

  // use this if any changes made to side nav tabs
  @Action(DeActivateSubsequentTabs)
  deActivateSubsequentTabs(ctx: StateContext<ProjectDetailsStateModel>, action: DeActivateSubsequentTabs) {
    const sideNav = ctx.getState().sideNav.map(ele =>
      action.tabs.includes(ele.id) ? { ...ele, selected: false, disabled: true } : ele
    );
    const patch: Partial<ProjectDetailsStateModel> = { sideNav: sideNav };
    if (action.tabs.includes(AppConstants.outputRecommendation)) {
      patch.selectedRecommendationNode = 'summary';
    }
    ctx.patchState(patch);
  }

  @Action(SetSelectedRecommendationNode)
  setSelectedRecommendationNode(ctx: StateContext<ProjectDetailsStateModel>, action: SetSelectedRecommendationNode) {
    ctx.patchState({ selectedRecommendationNode: action.nodeId });
  }

  /**
   * Toggling the Output view (with no explicit nodeId) always moves focus back to Recommendation
   * Summary, rather than leaving the user on whatever node happened to be selected in the other
   * view (e.g. a cluster-group node has no meaning in Workload View). A caller that already knows
   * exactly where it wants to land (e.g. the Recommendation Summary banner's Details button)
   * passes an explicit nodeId instead, so both the view switch and the selection land in one
   * reducer pass.
   */
  @Action(SetOutputView)
  setOutputView(ctx: StateContext<ProjectDetailsStateModel>, action: SetOutputView) {
    const nodeId = action.nodeId ?? 'summary';
    ctx.patchState({ outputView: action.view, selectedRecommendationNode: nodeId });
  }

   // use this to enable select tabs
  @Action(ActivateSelectTabs)
  activateSelectTabs(ctx: StateContext<ProjectDetailsStateModel>, action: ActivateSelectTabs) {
    const sideNav = ctx.getState().sideNav.map(ele =>
      action.tabs.includes(ele.id)
        ? { ...ele, disabled: false, selected: false }
        : { ...ele, disabled: true, selected: false }
    );
    this.store.dispatch(new ActivateSelectedTab(action.tabs[action.tabs.length - 1]));
    ctx.patchState({ sideNav: sideNav });
  }

  @Action(GetComponents)
  getComponents(ctx: StateContext<ProjectDetailsStateModel>, action: GetComponents) {
    if (action.productName != undefined && action.version != undefined && action.domain != undefined) {
      return this.dataProviderConfigService.getComponents(action).pipe(
        tap((response: any) => {
          if (action.domain == DOMAIN_TYPE.WorkloadDomain){
            ctx.patchState({ wldComponents: response });
          }
          else if (action.domain == DOMAIN_TYPE.ManagementDomain) {
            ctx.patchState({ mgntComponents: response });
          }
        })
      );
    }
    return EMPTY;
  }

  @Action(GetWorkloadInputConfigiuration, { cancelUncompleted: true })
  getWorkloadInputConfiguration(ctx: StateContext<ProjectDetailsStateModel>, action: GetWorkloadInputConfigiuration) {
    return this.dataProviderConfigService.getWorkloadInputConfiguration(action).pipe(
    tap((response: any) => {
        if (action.domain == DOMAIN_TYPE.WorkloadDomain){
          ctx.patchState({ workloadInputConfiguration: response });
        }
        else if (action.domain == DOMAIN_TYPE.ManagementDomain) {
          ctx.patchState({ mgmtDomainInputConfiguration: response });
        }
      }),
      catchError((error) => {
        console.error('Failed to fetch workload input configuration', error);
        return of(undefined);
      })
    );
  }

  @Action(GetDhciInputConfiguration, { cancelUncompleted: true })
  getDhciInputConfiguration(ctx: StateContext<ProjectDetailsStateModel>, action: GetDhciInputConfiguration) {
    return this.dataProviderConfigService.getDhciInputConfiguration(action).pipe(
      tap((response) => {
        // Same object reference on every cached replay - patching it is a no-op for selectors.
        if (ctx.getState().dhciInputConfiguration !== response) {
          ctx.patchState({ dhciInputConfiguration: response });
        }
      }),
      catchError((error) => {
        // Non-fatal: forms simply keep the HCI-derived limits/defaults.
        console.error('Failed to fetch dHCI input configuration', error);
        return of(undefined);
      })
    );
  }

  @Action(GetHftRaidsInputConfigiuration, { cancelUncompleted: true })
  getHftRaidsInputConfiguration(ctx: StateContext<ProjectDetailsStateModel>, action: GetHftRaidsInputConfigiuration) {
    return this.dataProviderConfigService.getHftRaidsInputConfiguration(action).pipe(
      tap((response: any) => {
        ctx.patchState({
          hftRaidsConfig: response.configurations,
          hftRaidsRequestContext: { clusterType: action.clusterType ?? 'HCI', hostFailuresToTolerate: action.hostfailurestotolerate },
        });
      }),
      catchError((error) => {
        console.error('Failed to fetch HFT/Raids configuration', error);
        return of(undefined);
      })
    );
  }

  @Action(GetIOProfileInputConfigiuration, { cancelUncompleted: true })
  getIoProfileInputConfiguration(ctx: StateContext<ProjectDetailsStateModel>, action: GetIOProfileInputConfigiuration) {
    return this.dataProviderConfigService.getIoProfileInputConfiguration(action).pipe(
      tap((response: any) => {
        ctx.patchState({ ioProfileConfig: response.configurations });
      }),
      catchError((error) => {
        console.error('Failed to fetch IO profile configuration', error);
        return of(undefined);
      })
    );
  }

  @Action(ResetHftRaidsAndIoProfileConfig)
  resetHftRaidsAndIoProfileConfig(ctx: StateContext<ProjectDetailsStateModel>) {
    ctx.patchState({ hftRaidsConfig: {}, hftRaidsRequestContext: undefined, ioProfileConfig: {} });
  }

  @Action(GetvSANOverheadsInputConfigiuration, { cancelUncompleted: true })
  getvSANOverheadsInputConfiguration(ctx: StateContext<ProjectDetailsStateModel>, action: GetvSANOverheadsInputConfigiuration) {
    return this.dataProviderConfigService.getvSANOverheadsInputConfiguration(action).pipe(
        tap((response: any) => {
        ctx.patchState({ vsanOverheadsConfig: response.configurations });
      })
    );
  }

  @Action(GetWorkloadTypeConfiguration, { cancelUncompleted: true })
  getWorkloadTypeConfiguration(ctx: StateContext<ProjectDetailsStateModel>, action: GetWorkloadTypeConfiguration) {
    return this.dataProviderConfigService.getWorkloadTypeConfiguration(action).pipe(
        tap((response: any) => {
        ctx.patchState({ workloadTypeConfiguration: response.configurations });
      })
    );
  }

  @Action(GetProductDetails)
  getProductDetailsConfig(ctx: StateContext<ProjectDetailsStateModel>) {
    return this.pcsizerService.getProductDetailsConfig().pipe(
      tap((response: any) => {
        ctx.patchState({ productConfig: response });
      })
    );
  }

  @Action(ResetState)
  resetState({ patchState }: StateContext<ProjectDetailsStateModel>) {
    patchState({
      sideNav: this.sideNavService.getInitState(),
      wldComponents: [],
      currentProject: undefined,
      projectId: '0'
    })
  }

  @Action(FetchProjectById, { cancelUncompleted: true })
  fetchProjectById(ctx: StateContext<ProjectDetailsStateModel>, action: FetchProjectById) {
    ctx.patchState({ projectId: action.projectId });
    return this.vcpmService.fetchProjectDataById(action.projectId).pipe(
      tap((projectData: any) => {
        if (String(ctx.getState().projectId) !== String(action.projectId)) return;
        projectData = this.projectMiddlewareService.applyProjectMigrations(projectData);
        ctx.patchState({
          currentProject: projectData,
          projectId: action.projectId
        })
      })
    );
  }

  @Action(FetchCustomerList)
  fetchCustomerList(ctx: StateContext<ProjectDetailsStateModel>, action: FetchCustomerList) {
    return this.dashboardService.getCustomerList(action.searchKey).pipe(
      tap((cusomerData: any) => {
        ctx.patchState({
          customerList: cusomerData
        })
      })
    );
  }

  @Action(UpdateProject, { cancelUncompleted: true })
  updateProject(ctx: StateContext<ProjectDetailsStateModel>, action: UpdateProject) {
    // Guard: Prevent updates in view-only or locked mode
    const currentProject = ctx.getState().currentProject;
    
    if (currentProject) {
      if (this.collaborationService.isEditorBlocked(currentProject)) {
        return throwError(() => new Error('Editing is unavailable until your lock is confirmed.'));
      }
      const isLocked = this.collaborationService.isAnalysisLocked(currentProject);
      const isViewOnly = this.collaborationService.isAnalysisSharedViewOnly(currentProject);
      
      if (isLocked || isViewOnly) {
        // Return empty observable - no API call
        return EMPTY;
      }
    }
    
    // Normal flow - proceed with update
    return this.collaborationService.validateBeforeWrite(currentProject).pipe(
      switchMap(() => this.vcpmService.updateProject(action.projectId, action.projectPayload, action.pushToSupercollider)),
      throwIfEmpty(() => new Error('Project was not saved. Please retry.')),
      tap((response: any) => {
        if (String(ctx.getState().currentProject?.projectId) !== String(currentProject?.projectId)) {
          throw new Error('Project changed before the save completed.');
        }
        ctx.patchState({
          currentProject: this.projectMiddlewareService.applyProjectMigrations(response)
        })
      })
    );
  }

  @Action(SaveProject, { cancelUncompleted: true })
  saveProject(ctx: StateContext<ProjectDetailsStateModel>, action: SaveProject) {
    // Guard: Prevent saves in view-only or locked mode
    const currentProject = ctx.getState().currentProject;
    
    if (currentProject) {
      if (this.collaborationService.isEditorBlocked(currentProject)) {
        return throwError(() => new Error('Editing is unavailable until your lock is confirmed.'));
      }
      const isLocked = this.collaborationService.isAnalysisLocked(currentProject);
      const isViewOnly = this.collaborationService.isAnalysisSharedViewOnly(currentProject);
      
      if (isLocked || isViewOnly) {
        return EMPTY;
      }
    }

    return this.collaborationService.validateBeforeWrite(currentProject).pipe(
      switchMap(() => this.vcpmService.saveProject(action.projectMetadata, action.projectData)),
      throwIfEmpty(() => new Error('Project was not saved. Please retry.')),
      tap((response: any) => {
        if (String(ctx.getState().currentProject?.projectId) !== String(currentProject?.projectId)) {
          throw new Error('Project changed before the save completed.');
        }
        ctx.patchState({
          currentProject: this.projectMiddlewareService.applyProjectMigrations(response),
          projectId: response.projectId
        })
      })
    );
  }

  @Action(UpdateProjectMetadata, { cancelUncompleted: true })
  updateProjectMetadata(ctx: StateContext<ProjectDetailsStateModel>, action: UpdateProjectMetadata) {
    // Guard: Prevent metadata updates in view-only or locked mode
    const currentProject = ctx.getState().currentProject;
    
    if (currentProject) {
      if (this.collaborationService.isEditorBlocked(currentProject)) {
        return throwError(() => new Error('Editing is unavailable until your lock is confirmed.'));
      }
      const isLocked = this.collaborationService.isAnalysisLocked(currentProject);
      const isViewOnly = this.collaborationService.isAnalysisSharedViewOnly(currentProject);
      
      if (isLocked || isViewOnly) {
        return EMPTY;
      }
    }

    return this.collaborationService.validateBeforeWrite(currentProject).pipe(
      switchMap(() => this.vcpmService.updateProjectMetadata(action.project)),
      throwIfEmpty(() => new Error('Project was not saved. Please retry.')),
      tap((response: any) => {
        if (String(ctx.getState().currentProject?.projectId) !== String(currentProject?.projectId)) {
          throw new Error('Project changed before the save completed.');
        }
        ctx.patchState({
          currentProject: this.projectMiddlewareService.applyProjectMigrations(response)
        })
      })
    );
  }

  @Action(SetVcenterHostCount)
  getVcenterHostCount(ctx: StateContext<ProjectDetailsStateModel>, action: SetVcenterHostCount) {
    ctx.patchState({ vCenterHostCount: action.hostCountModalObject });
  }

  @Action(RefreshProjectCollaboration)
  refreshProjectCollaboration(ctx: StateContext<ProjectDetailsStateModel>, action: RefreshProjectCollaboration) {
    const current = ctx.getState().currentProject;
    if (!current || String(current.projectId) !== String(action.project.projectId)) return;
    const { locked, lockedBy, sharingData, currentOwner, createdBy } = action.project;
    ctx.patchState({ currentProject: { ...current, locked, lockedBy, sharingData, currentOwner, createdBy } });
  }

  @Action(LockProject)
  lockProject(ctx: StateContext<ProjectDetailsStateModel>, action: LockProject) {
    if (!ctx.getState().currentProject) return EMPTY;
    return this.vcpmService.lockUnlockProject(action.projectId, 'lock', action.isAdminUserRequest).pipe(
      tap(response => this.refreshProjectCollaboration(ctx, new RefreshProjectCollaboration(response)))
    );
  }

  @Action(UnlockProject)
  unlockProject(ctx: StateContext<ProjectDetailsStateModel>, action: UnlockProject) {
    return this.vcpmService.lockUnlockProject(action.projectId, 'unlock', action.isAdminUserRequest).pipe(
      tap(response => this.refreshProjectCollaboration(ctx, new RefreshProjectCollaboration(response)))
    );
  }

  @Action(GetProductOfferings)
  getProductOfferings(ctx: StateContext<ProjectDetailsStateModel>) {
    return this.dashboardService.getProductOfferings().pipe(
      tap((response: VCPM.ProductOffering[]) => {
        ctx.patchState({
          productOffering: response
        })
      })
    );
  }

  @Action(UpdateWldSwComponents, { cancelUncompleted: true })
  updateWldSwComponents(ctx: StateContext<ProjectDetailsStateModel>, action: UpdateWldSwComponents){
    if (!ctx.getState().currentProject) {
      return EMPTY;
    }
    let project = JSON.parse(JSON.stringify(ctx.getState().currentProject));
    let projectData = JSON.parse(JSON.stringify(ctx.getState().currentProject?.data));
    let wld = projectData.workloadDomains || [];
    const domainIdIndex = wld.findIndex((w: any) => typeof w === 'object' && w !== null && w.hasOwnProperty('id') && w.id === action.tabId);
    if (domainIdIndex === -1) {
      return EMPTY;
    }
    wld[domainIdIndex] = {
      ...(wld[domainIdIndex] || {}),
      selectedComponents: action.swComponentsData
    };
    if(action.inputsChangedInWLD) {
      project.metadata.currentTab = AppConstants.wldDomainSizing;
      project.metadata.status = AppConstants.PROJECT_STATUS_INPROGRESS;
      projectData.output = undefined;
    }
    project.data = projectData;
    ctx.patchState({
      workloadDomains:wld,
      currentProject: project
    });
    return this.store.dispatch(new UpdateProject(ctx.getState().currentProject?.projectId as string, project));
  }

  // Local contexts defer reconciliation until all commands in a workload transaction settle.
  private readonly stagedWorkloadContexts = new WeakSet<object>();

  @Action(SaveWorkloadConfiguration, { cancelUncompleted: true })
  saveWorkloadConfiguration(ctx: StateContext<ProjectDetailsStateModel>, action: SaveWorkloadConfiguration) {
    const original = ctx.getState().currentProject;
    if (!original) return throwError(() => new Error('Project is no longer open.'));
    if (!original.data.workloadDomains?.some(d => d.id === action.domainId)
      || hasInvalidClusterReuse(original, action.domainId, action.workloadList)) {
      return throwError(() => new Error('Select an available cluster before saving workloads.'));
    }
    let staged: ProjectDetailsStateModel = { ...ctx.getState(), currentProject: JSON.parse(JSON.stringify(original)) as VCPM.Project };
    const local: StateContext<ProjectDetailsStateModel> = {
      abortSignal: ctx.abortSignal,
      dispatch: () => throwError(() => new Error('Intermediate dispatch is not allowed in a workload transaction.')),
      setState: value => { staged = typeof value === 'function' ? value(staged) : value; },
      getState: () => staged,
      patchState: (patch: Partial<ProjectDetailsStateModel>) => { staged = { ...staged, ...patch }; return staged; },
    };
    this.stagedWorkloadContexts.add(local);
    const workloads: VCPM.Workload[] = JSON.parse(JSON.stringify(action.workloadList));
    try {
      // Reuse the existing reducers against an isolated context; never dispatch intermediate actions.
      const commands: WorkloadClusterCommand[] = action.commands.map(command => Object.assign(Object.create(Object.getPrototypeOf(command)), JSON.parse(JSON.stringify(command)), { skipPersist: true }));
      for (const command of commands) {
        const isStorage = command instanceof CreateStorageCluster;
        if (command instanceof CreateHciCluster) this.createHciCluster(local, command);
        else if (command instanceof CreateComputeCluster) this.createComputeCluster(local, command);
        else if (isStorage) this.createStorageCluster(local, command);
        else continue;
        const workload = workloads.find(w => w.id === command.workloadId);
        const reference = staged.lastCreatedClusterReference;
        if (!workload || !reference) throw new Error('Cluster creation did not complete.');
        if (isStorage) workload.storageCluster = { ...reference };
        else workload.computeCluster = { ...reference };
      }
      this.updateWldList(local, new UpdateWldList(workloads, action.domainId, true));
      // Compute updates fan out before any storage changes are reconciled.
      for (const command of commands) {
        if (command instanceof UpdateHciClusterConfiguration) this.updateHciClusterConfiguration(local, command);
        else if (command instanceof UpdateComputeClusterConfiguration) this.updateComputeClusterConfiguration(local, command);
      }
      for (const command of commands) {
        if (command instanceof UpdateStorageClusterConfiguration) this.updateStorageClusterConfiguration(local, command);
      }
      const project = staged.currentProject!;
      collectOrphanedClustersAndOwners(project.data);
      reconcileStorageStretch(project, original.data);
      const errors = getStorageStretchErrors(project.data);
      if (errors.length) throw new Error(errors.join(' '));
      syncManagementClusterType(project.data);
      project.data.output = undefined;
      // UpdateProject publishes only the successful server response; failed saves retain original state.
      return this.store.dispatch(new UpdateProject(String(project.projectId), project)).pipe(tap(() => {
        const saved = ctx.getState().currentProject;
        ctx.patchState({ workloadDomains: saved?.data.workloadDomains, managementDomain: saved?.data.managementDomain, summary: undefined });
      }));
    } catch (error) {
      return throwError(() => error);
    } finally {
      this.stagedWorkloadContexts.delete(local);
    }
  }

  @Action(UpdateWldList, { cancelUncompleted: true })
  updateWldList(ctx: StateContext<ProjectDetailsStateModel>, action: UpdateWldList){
    if (!ctx.getState().currentProject) {
      return EMPTY;
    }
    let project: VCPM.Project = JSON.parse(JSON.stringify(ctx.getState().currentProject));
    let projectData: VCPM.ProjectData = JSON.parse(JSON.stringify(ctx.getState().currentProject?.data));
    const wld = projectData.workloadDomains ? projectData.workloadDomains : [];
    const domainIdIndex = wld.findIndex((w: any) => w.id === action.domainId);
    if (domainIdIndex === -1) {
      return EMPTY;
    }
    const domain = wld[domainIdIndex];
    if (!domain) {
      return EMPTY;
    }
    domain.workloadList = action.workloadList;
    wld[domainIdIndex] = domain;
    
    project.data = projectData;
    if (!this.stagedWorkloadContexts.has(ctx)) reconcileStorageStretch(project, ctx.getState().currentProject?.data);
    syncManagementClusterType(projectData);
    
    projectData.workloadDomains = wld;
    projectData.output = undefined;
    project.data = projectData;
    ctx.patchState({
      currentProject: project,
      workloadDomains: wld,
      managementDomain: projectData.managementDomain
    });
    project.metadata.currentTab = AppConstants.wldDomainSizing;
    project.metadata.status = AppConstants.PROJECT_STATUS_INPROGRESS;
    if (action.skipPersist) {
      return EMPTY;
    }
    return this.store.dispatch(new UpdateProject(ctx.getState().projectId, project));
  }

  // vSAN Max (dHCI): shared "Use Existing Cluster" reusable-entity persistence (Create/Update per
  // entity type). `Create*` assigns `name` from its own project-wide counter and never accepts one;
  // `Update*` payloads have no `name` field at all - id/name immutability enforced at the type level.
  // `Update*` also performs the fan-out sync: every other workload in scope that references the
  // entity gets its own local mirror fields refreshed in the same patchState/UpdateProject dispatch,
  // so a failed save leaves nothing partially applied locally. The sync itself
  // (syncReferencedHardwareConfiguration) is a pure pcs-domain function, shared with the output-side
  // cluster recalculation flow (RecalculateClusterHardwareConfiguration below).

  @Action(CreateHciCluster, { cancelUncompleted: true })
  createHciCluster(ctx: StateContext<ProjectDetailsStateModel>, action: CreateHciCluster) {
    const currentProject = ctx.getState().currentProject;
    if (!currentProject) {
      return EMPTY;
    }
    const project: VCPM.Project = JSON.parse(JSON.stringify(currentProject));
    const domain = project.data.workloadDomains?.find((d) => d.id === action.domainId);
    if (!domain) {
      return EMPTY;
    }
    const nextNumber = project.data.nextHciClusterNumber ?? 1;
    const cluster: VCPM.HCICluster = {
      type: 'HCI',
      id: crypto.randomUUID(),
      name: buildNewClusterName('HCI', nextNumber),
      vsanArchitecture: action.vsanArchitecture,
      hardwareConfiguration: action.hardwareConfiguration,
      clusterConfiguration: action.clusterConfiguration,
    };
    domain.clusterConfigurations = domain.clusterConfigurations ?? [];
    domain.clusterConfigurations.push(cluster);
    project.data.nextHciClusterNumber = nextNumber + 1;
    ctx.patchState({ currentProject: project, lastCreatedClusterReference: { id: cluster.id, name: cluster.name } });
    return action.skipPersist ? EMPTY : this.store.dispatch(new UpdateProject(ctx.getState().projectId, project));
  }


  @Action(UpdateHciClusterConfiguration, { cancelUncompleted: true })
  updateHciClusterConfiguration(ctx: StateContext<ProjectDetailsStateModel>, action: UpdateHciClusterConfiguration) {
    const currentProject = ctx.getState().currentProject;
    if (!currentProject) {
      return EMPTY;
    }
    const project: VCPM.Project = JSON.parse(JSON.stringify(currentProject));
    const domain = project.data.workloadDomains?.find((d) => d.id === action.domainId);
    const cluster = domain?.clusterConfigurations?.find((c) => c.id === action.clusterId && c.type === 'HCI') as VCPM.HCICluster | undefined;
    if (!domain || !cluster) {
      return EMPTY;
    }
    cluster.hardwareConfiguration = action.hardwareConfiguration;
    cluster.clusterConfiguration = action.clusterConfiguration;

    // An HCI cluster can be referenced by other HCI workloads AND by dHCI workloads that picked
    // it as their Compute side - both go through the single computeCluster reference now, so
    // vcfDeploymentInputs.deploymentType (not which reference field matched) decides the sync
    // target. No workload outside this domain can reference it (Compute reuse is domain-scoped),
    // so scanning this domain's workloadList only is sufficient.
    domain.workloadList?.forEach((workload) => {
      if (workload.computeCluster?.id !== cluster.id) {
        return;
      }
      if (workload.vcfDeploymentInputs?.deploymentType === 'dHCI') {
        workload.computeClusterHardwareConfiguration = syncReferencedHardwareConfiguration(workload.computeClusterHardwareConfiguration, cluster.hardwareConfiguration);
        workload.computeClusterConfiguration = toComputeClusterConfiguration(cluster.clusterConfiguration);
      } else {
        workload.hardwareConfiguration = syncReferencedHardwareConfiguration(workload.hardwareConfiguration, cluster.hardwareConfiguration);
        workload.clusterConfiguration = cluster.clusterConfiguration;
      }
    });

    return this.persistClusterEntityUpdate(ctx, project, action.skipPersist);
  }

  @Action(CreateComputeCluster, { cancelUncompleted: true })
  createComputeCluster(ctx: StateContext<ProjectDetailsStateModel>, action: CreateComputeCluster) {
    const currentProject = ctx.getState().currentProject;
    if (!currentProject) {
      return EMPTY;
    }
    const project: VCPM.Project = JSON.parse(JSON.stringify(currentProject));
    const domain = project.data.workloadDomains?.find((d) => d.id === action.domainId);
    if (!domain) {
      return EMPTY;
    }
    const nextNumber = project.data.nextComputeClusterNumber ?? 1;
    const cluster: VCPM.ComputeCluster = {
      type: 'COMPUTE',
      id: crypto.randomUUID(),
      name: buildNewClusterName('COMPUTE', nextNumber),
      vsanArchitecture: action.vsanArchitecture,
      hardwareConfiguration: action.hardwareConfiguration,
      clusterConfiguration: action.clusterConfiguration,
    };
    domain.clusterConfigurations = domain.clusterConfigurations ?? [];
    domain.clusterConfigurations.push(cluster);
    project.data.nextComputeClusterNumber = nextNumber + 1;
    ctx.patchState({ currentProject: project, lastCreatedClusterReference: { id: cluster.id, name: cluster.name } });
    return action.skipPersist ? EMPTY : this.store.dispatch(new UpdateProject(ctx.getState().projectId, project));
  }

  @Action(UpdateComputeClusterConfiguration, { cancelUncompleted: true })
  updateComputeClusterConfiguration(ctx: StateContext<ProjectDetailsStateModel>, action: UpdateComputeClusterConfiguration) {
    const currentProject = ctx.getState().currentProject;
    if (!currentProject) {
      return EMPTY;
    }
    const project: VCPM.Project = JSON.parse(JSON.stringify(currentProject));
    const domain = project.data.workloadDomains?.find((d) => d.id === action.domainId);
    const cluster = domain?.clusterConfigurations?.find((c) => c.id === action.clusterId && c.type === 'COMPUTE') as VCPM.ComputeCluster | undefined;
    if (!domain || !cluster) {
      return EMPTY;
    }
    cluster.hardwareConfiguration = action.hardwareConfiguration;
    cluster.clusterConfiguration = action.clusterConfiguration;

    // No HCI-deployment workload can ever reference a ComputeCluster (asymmetric pool rule) -
    // every workload referencing this cluster via computeCluster is necessarily dHCI, so no
    // deploymentType branch is needed here (unlike updateHciClusterConfiguration above).
    domain.workloadList?.forEach((workload) => {
      if (workload.computeCluster?.id === cluster.id) {
        workload.computeClusterHardwareConfiguration = syncReferencedHardwareConfiguration(workload.computeClusterHardwareConfiguration, cluster.hardwareConfiguration);
        workload.computeClusterConfiguration = cluster.clusterConfiguration;
      }
    });

    return this.persistClusterEntityUpdate(ctx, project, action.skipPersist);
  }

  @Action(CreateStorageCluster, { cancelUncompleted: true })
  createStorageCluster(ctx: StateContext<ProjectDetailsStateModel>, action: CreateStorageCluster) {
    const currentProject = ctx.getState().currentProject;
    if (!currentProject) {
      return EMPTY;
    }
    const project: VCPM.Project = JSON.parse(JSON.stringify(currentProject));
    const nextNumber = project.data.nextStorageClusterNumber ?? 1;
    const cluster: VCPM.StorageCluster = {
      type: 'STORAGE',
      id: crypto.randomUUID(),
      name: buildNewClusterName('STORAGE', nextNumber),
      vsanArchitecture: action.vsanArchitecture,
      workloadDomainId: action.domainId,
      hardwareConfiguration: action.hardwareConfiguration,
      clusterConfiguration: action.clusterConfiguration,
    };
    project.data.clusterConfigurations = project.data.clusterConfigurations ?? [];
    project.data.clusterConfigurations.push(cluster);
    project.data.nextStorageClusterNumber = nextNumber + 1;
    ctx.patchState({ currentProject: project, lastCreatedClusterReference: { id: cluster.id, name: cluster.name } });
    return action.skipPersist ? EMPTY : this.store.dispatch(new UpdateProject(ctx.getState().projectId, project));
  }

  @Action(UpdateStorageClusterConfiguration, { cancelUncompleted: true })
  updateStorageClusterConfiguration(ctx: StateContext<ProjectDetailsStateModel>, action: UpdateStorageClusterConfiguration) {
    const currentProject = ctx.getState().currentProject;
    if (!currentProject) {
      return EMPTY;
    }
    const project: VCPM.Project = JSON.parse(JSON.stringify(currentProject));
    const cluster = project.data.clusterConfigurations?.find((c) => c.id === action.clusterId);
    if (!cluster) {
      return EMPTY;
    }
    cluster.hardwareConfiguration = action.hardwareConfiguration;
    cluster.clusterConfiguration = action.clusterConfiguration;

    // Storage reuse is project-wide, unlike Compute - scan every domain's workloadList.
    project.data.workloadDomains?.forEach((domain) => {
      domain.workloadList?.forEach((workload) => {
        if (workload.storageCluster?.id === cluster.id) {
          workload.storageClusterHardwareConfiguration = syncReferencedHardwareConfiguration(workload.storageClusterHardwareConfiguration, cluster.hardwareConfiguration);
          workload.storageClusterConfiguration = cluster.clusterConfiguration;
        }
      });
    });

    return this.persistClusterEntityUpdate(ctx, project, action.skipPersist);
  }

  // Shared tail of the Update*ClusterConfiguration actions: a stretch flip on a shared HCI/Compute/
  // Storage entity can change storage dependencies and management topology; synchronize both
  // and invalidate output when shared input changes.
  private persistClusterEntityUpdate(ctx: StateContext<ProjectDetailsStateModel>, project: VCPM.Project, skipPersist = false) {
    if (!this.stagedWorkloadContexts.has(ctx)) reconcileStorageStretch(project, ctx.getState().currentProject?.data);
    const sharedInputsChanged = JSON.stringify(project.data.workloadDomains) !== JSON.stringify(ctx.getState().currentProject?.data.workloadDomains);
    if (sharedInputsChanged) project.data.output = undefined;
    const mgmtChanged = syncManagementClusterType(project.data);
    if (mgmtChanged) {
      project.data.output = undefined;
      ctx.patchState({ currentProject: project, workloadDomains: project.data.workloadDomains, managementDomain: project.data.managementDomain });
    } else {
      ctx.patchState({ currentProject: project, workloadDomains: project.data.workloadDomains });
    }
    return skipPersist ? EMPTY : this.store.dispatch(new UpdateProject(ctx.getState().projectId, project));
  }

  // vSAN Max (dHCI) Risk #9: orphaning can now happen two ways - a workload delete, or a save
  // where clear-on-toggle (component §3) nulled a reference. Both funnel through the same save
  // path (handleWorkloadFormUpdate), so this single sweep after every save covers both triggers.
  @Action(GarbageCollectOrphanedClusters, { cancelUncompleted: true })
  garbageCollectOrphanedClusters(ctx: StateContext<ProjectDetailsStateModel>, action: GarbageCollectOrphanedClusters) {
    const currentProject = ctx.getState().currentProject;
    if (!currentProject) {
      return EMPTY;
    }
    const project: VCPM.Project = JSON.parse(JSON.stringify(currentProject));

    if (!collectOrphanedClustersAndOwners(project.data)) return EMPTY;
    syncManagementClusterType(project.data);
    ctx.patchState({ currentProject: project, workloadDomains: project.data.workloadDomains,
      managementDomain: project.data.managementDomain, summary: undefined });
    return action.skipPersist ? EMPTY : this.store.dispatch(new UpdateProject(ctx.getState().projectId, project));
  }

  // Persists a Recalculate-against-a-customized-ReadyNode result as the workload's "4th card"
  // (selectedReadyNodeRecommendation), makes it the active selection, AND writes the exact same
  // hardwareConfiguration through to the workload's INPUT side (replacing whatever was there,
  // including converting a No-Preference workload to readynode-based) - so input and output always
  // agree on which ReadyNode is selected, and reopening the input wizard shows it. See
  // ReadyNodeRecalculationService for how `action.hardwareConfiguration` is built.
  @Action(UpdateWorkloadReadyNodeRecommendation, { cancelUncompleted: true })
  updateWorkloadReadyNodeRecommendation(ctx: StateContext<ProjectDetailsStateModel>, action: UpdateWorkloadReadyNodeRecommendation) {
    const currentProject = ctx.getState().currentProject;
    const recommendations = currentProject?.data?.output?.recommendation?.workloadDomainsRecommendation?.result?.recommendations;
    if (!currentProject || !recommendations) {
      return EMPTY;
    }
    const project: VCPM.Project = JSON.parse(JSON.stringify(currentProject));
    const domainRecomm = project.data.output!.recommendation!.workloadDomainsRecommendation!.result.recommendations
      .find((r: PcsSizingEngineResponse.Recommendation) => r.requirementsId === action.requirementsId);
    const workloadRecomm = domainRecomm?.workloadRecommendations.find((w: PcsSizingEngineResponse.WorkloadRecommendation) => w.workloadId === action.workloadId);
    if (!workloadRecomm) {
      return EMPTY;
    }
    workloadRecomm.selectedReadyNodeRecommendation = {
      hardwareConfiguration: action.hardwareConfiguration,
      result: action.result,
      recalculatedAt: new Date().toISOString(),
      clusterType: action.clusterType,
    };
    workloadRecomm.selectedProfileCode = AppConstants.SELECTED_READYNODE_PROFILE_CODE;

    const workloadInput = project.data.workloadDomains
      ?.find((d) => String(d.id) === String(action.requirementsId))
      ?.workloadList?.find((w) => String(w.id) === String(action.workloadId));
    if (workloadInput) {
      workloadInput.hardwareConfiguration = action.hardwareConfiguration;
    }

    // Rebuild the summary here (rather than nulling it and relying on StdGfRecommendationComponent
    // to notice and rebuild it) - its @Input() project is only ever assigned once by
    // RecommendationComponent's constructor snapshot, so ngOnChanges never re-fires after this
    // save, and the left-nav "Clusters" tree (which reads this summary field, not currentProject)
    // was staying permanently empty for the rest of the session.
    const freshSummary = this.buildSummarySafely(project, ctx.getState().productOffering);
    project.data.output!.summary = freshSummary;

    ctx.patchState({ currentProject: project, summary: freshSummary });
    return this.persistAndKeepEngineDerivedOutput(ctx, project);
  }

  // Cluster View's cluster-scoped counterpart to updateWorkloadReadyNodeRecommendation above -
  // persists a Recalculate result (ReadyNode for HCI/SON, ESXi for CON) onto the cluster entity's
  // own canonical hardwareConfiguration AND fans it out to every workload referencing that cluster.
  // Superseded as a direct UI dispatch by CommitOutputSelections/commitOutputSelections (Cluster
  // View's Save now stages a 'cluster-readynode' change via OutputStagedChangesService instead of
  // dispatching this directly - see WorkloadDomainComponent.handleClusterRecalculationSaveModal) but
  // left in place, same as UpdateWorkloadReadyNodeRecommendation/
  // UpdateManagementDomainReadyNodeRecommendation above - the actual mutation now lives in
  // applyClusterReadyNodeChange (output-staged-changes.service.ts), shared with
  // commitOutputSelections's own 'cluster-readynode' branch so the two paths can never drift.
  @Action(RecalculateClusterHardwareConfiguration, { cancelUncompleted: true })
  recalculateClusterHardwareConfiguration(ctx: StateContext<ProjectDetailsStateModel>, action: RecalculateClusterHardwareConfiguration) {
    const currentProject = ctx.getState().currentProject;
    if (!currentProject) {
      return EMPTY;
    }
    const project = applyStagedChangeToProject(JSON.parse(JSON.stringify(currentProject)), {
      kind: 'cluster-readynode',
      requirementsId: action.requirementsId,
      clusterId: action.clusterId,
      clusterKind: action.clusterKind,
      hardwareConfiguration: action.hardwareConfiguration,
      resultsByWorkloadId: action.resultsByWorkloadId,
    });
    if (!project) {
      return EMPTY;
    }

    // Rebuild the summary here rather than nulling it - see the doc comment on the equivalent line
    // in updateWorkloadReadyNodeRecommendation() above for why relying on a downstream component to
    // rebuild it left the left-nav "Clusters" tree permanently empty after a cluster-scoped save.
    if (project.data.output) {
      const freshSummary = this.buildSummarySafely(project, ctx.getState().productOffering);
      project.data.output.summary = freshSummary;
      ctx.patchState({ currentProject: project, summary: freshSummary });
    } else {
      ctx.patchState({ currentProject: project });
    }
    return this.persistAndKeepEngineDerivedOutput(ctx, project);
  }

  // NOTE: the management domain always has exactly one workload recommendation, addressed
  // positionally ([0]) - same as every other read of this data in ManagementDomainComponent.
  // A prior version matched by `w.workloadId === 'MGMT'`, a hardcoded literal never actually
  // derived from the live recommendation - when it didn't match, `find()` silently returned
  // undefined, this whole action bailed via `return EMPTY`, and UpdateProject was never
  // dispatched, so the Recalculate result never reached the backend and vanished on refresh
  // (the optimistic in-memory mutation in ManagementDomainComponent.onRecalculate() masked this,
  // since it renders the "Selected ReadyNode" card regardless of whether persistence succeeded).
  @Action(UpdateManagementDomainReadyNodeRecommendation, { cancelUncompleted: true })
  updateManagementDomainReadyNodeRecommendation(ctx: StateContext<ProjectDetailsStateModel>, action: UpdateManagementDomainReadyNodeRecommendation) {
    const currentProject = ctx.getState().currentProject;
    const mgmtRecomm = currentProject?.data?.output?.recommendation?.managementDomainRecommendation?.result?.recommendations?.[0];
    const workloadRecomm = mgmtRecomm?.workloadRecommendations?.[0];
    if (!currentProject || !workloadRecomm) {
      return EMPTY;
    }
    const project: VCPM.Project = JSON.parse(JSON.stringify(currentProject));
    const targetWorkloadRecomm = project.data.output!.recommendation!.managementDomainRecommendation!.result.recommendations[0]
      .workloadRecommendations[0];
    targetWorkloadRecomm.selectedReadyNodeRecommendation = {
      hardwareConfiguration: action.hardwareConfiguration,
      result: action.result,
      recalculatedAt: new Date().toISOString(),
    };
    targetWorkloadRecomm.selectedProfileCode = AppConstants.SELECTED_READYNODE_PROFILE_CODE;

    // Write the same hardwareConfiguration through to the management domain's INPUT side too -
    // mirrors the workload-domain reducer above, keeping input/output always in sync.
    if (project.data.managementDomain?.configuration) {
      project.data.managementDomain.configuration.hardwareConfiguration = action.hardwareConfiguration;
    }

    // Rebuild the summary here rather than nulling it - see the doc comment on the equivalent line
    // in updateWorkloadReadyNodeRecommendation() above for why relying on a downstream component to
    // rebuild it left the left-nav "Clusters" tree permanently empty after a save.
    const freshSummary = this.buildSummarySafely(project, ctx.getState().productOffering);
    project.data.output!.summary = freshSummary;

    ctx.patchState({ currentProject: project, summary: freshSummary });
    return this.persistAndKeepEngineDerivedOutput(ctx, project);
  }

  // Commits ONE staged output-page selection (a tier profile pick, or a ReadyNode recalculate
  // preview) for the tab the user is currently on - see OutputStagedChangesService. Replaces the
  // per-card ReadyNode Save button/modal that used to dispatch UpdateWorkloadReadyNodeRecommendation
  // / UpdateManagementDomainReadyNodeRecommendation directly; those two actions are left in place
  // (still exercised by this reducer's 'readynode' branch logic below) but are no longer dispatched
  // from the UI on their own.
  //
  // A 'profile' change (Small/Medium/Large) is NOT tied to any hardware SKU, so saving one flips the
  // workload/management-domain input to hardwareConfigMode 'no-preference' and discards any saved
  // ReadyNode customization (selectedReadyNodeRecommendation) - there is no multi-iteration history,
  // so the previous ReadyNode pick simply doesn't survive a tier save. The rewritten
  // hardwareConfiguration MUST retain the existing commonConfig, or project-schema.transform.ts's
  // legacy-flat migration guard zeroes it on next load (no commonConfig + no cpuBasedConfig/
  // readyNodeBasedConfig/mode looks like a pre-mode-field legacy project to that guard).
  @Action(CommitOutputSelections, { cancelUncompleted: true })
  commitOutputSelections(ctx: StateContext<ProjectDetailsStateModel>, action: CommitOutputSelections) {
    const currentProject = ctx.getState().currentProject;
    if (!currentProject) {
      return EMPTY;
    }
    const project = applyStagedChangeToProject(JSON.parse(JSON.stringify(currentProject)), action.change);
    if (!project) {
      return EMPTY;
    }

    // Recompute the summary as part of THIS save, rather than nulling it and hoping a page visit
    // to StdGfRecommendationComponent recomputes it before the next export - see the doc comment on
    // buildSummarySafely above for why relying on a downstream component to rebuild it left the
    // left-nav "Clusters" tree permanently empty.
    const summary = this.buildSummarySafely(project, ctx.getState().productOffering);
    if (project.data.output) {
      project.data.output.summary = summary;
    }

    ctx.patchState({ currentProject: project, summary });
    return this.persistAndKeepEngineDerivedOutput(ctx, project);
  }

  @Action(UpdateManagementDomainSwComponents, { cancelUncompleted: true })
  updateManagementDomainSwComponents(ctx: StateContext<ProjectDetailsStateModel>, action: UpdateManagementDomainSwComponents){
    let project = JSON.parse(JSON.stringify(ctx.getState().currentProject));
    let projectData: VCPM.ProjectData = JSON.parse(JSON.stringify(ctx.getState().currentProject?.data));
    let managementDomain = projectData.managementDomain ? projectData.managementDomain: {} as VCPM.ManagementDomain;
    let summary = projectData.output?.summary;
    managementDomain.selectedComponents = action.swComponentsData;
    projectData.managementDomain = managementDomain;
    if(action.inputsChangedInMgmtDomain) {
      project.metadata.currentTab = AppConstants.mgntDomainSizing;
      project.metadata.status = AppConstants.PROJECT_STATUS_INPROGRESS;
      if(projectData.output?.recommendation?.managementDomainRecommendation) {
        projectData.output.recommendation.managementDomainRecommendation = undefined;
        projectData.output.summary = undefined;
        summary = undefined;
      }
    }
    project.data = projectData;
    ctx.patchState({
      managementDomain: managementDomain,
      currentProject: project,
      summary: summary
    });
    return this.store.dispatch(new UpdateProject(ctx.getState().projectId, project));
  }

  @Action(UpdateManagementDomainConfiguration, { cancelUncompleted: true })
  updateManagementDomainConfiguration(ctx: StateContext<ProjectDetailsStateModel>, action: UpdateManagementDomainConfiguration){
    let project: VCPM.Project = JSON.parse(JSON.stringify(ctx.getState().currentProject));
    let projectData = JSON.parse(JSON.stringify(ctx.getState().currentProject?.data));
    let managementDomain = projectData.managementDomain? projectData.managementDomain: {};
    let summary = projectData.output?.summary;
    managementDomain.configuration = action.configuration;
    projectData.managementDomain = managementDomain;
    project.data = projectData;
    
    // Update currentTab to mgnt-domain-sizing when configuration is saved
    project.metadata.currentTab = AppConstants.mgntDomainSizing;
    project.metadata.status = AppConstants.PROJECT_STATUS_INPROGRESS;
    
    if(project?.data?.output?.recommendation?.managementDomainRecommendation) {
      project.data.output.recommendation.managementDomainRecommendation = undefined;
      project.data.output.summary = undefined;
      summary = undefined;
    }
    ctx.patchState({
      managementDomain:managementDomain,
      currentProject: project,
      summary: summary
    });
    if (action.save === false) {
      // Local-only patch: caller (e.g. onNext's recommendation flow) will fold this
      // change into a later save instead of persisting it here.
      return;
    }
    return this.store.dispatch(new UpdateProject(ctx.getState().projectId, project));
  }

  @Action(UpdateDomainName, { cancelUncompleted: true })
  updateDomainName(ctx: StateContext<ProjectDetailsStateModel>, action: UpdateDomainName) {
    const currentProject: VCPM.Project = JSON.parse(JSON.stringify(ctx.getState().currentProject));
    const wlds = currentProject?.data.workloadDomains || [];
    const existingDomainIndex = wlds.findIndex(
      (w: any) => w.id === action.tabId
    );

    const updatedWld: VCPM.WorkloadDomain = {
      id: action.tabId,
      domainName: action.domainName,
      domainType: action.domainType,
      selectedComponents: {
        swComponents: undefined
      },
      workloadList: []
    };

    if (existingDomainIndex === -1) {
      let workloadDomains = [...wlds, updatedWld];
      if(currentProject) {
        currentProject.data.workloadDomains = workloadDomains;
        currentProject.data.output = undefined;
        ctx.patchState({
          currentProject: currentProject,
          workloadDomains: workloadDomains
        });
        currentProject.metadata.currentTab = AppConstants.wldDomainSizing;
        currentProject.metadata.status = AppConstants.PROJECT_STATUS_INPROGRESS;
      }
    } else {
      const updatedWlds = [...wlds];
      // Rename only touches name/type: spread the existing domain so every other field survives. Rebuilding
      // it from scratch used to drop clusterConfigurations, orphaning the HCI/Compute clusters the
      // workloads still reference (blank hardware-first chassis, read-only ReadyNode Recommender).
      updatedWlds[existingDomainIndex] = {
        ...updatedWlds[existingDomainIndex],
        domainName: updatedWld.domainName,
        domainType: updatedWld.domainType,
      };
      if(currentProject) {
        currentProject.data.workloadDomains = updatedWlds;
        ctx.patchState({
          currentProject: currentProject,
          workloadDomains: updatedWlds
        });
        currentProject.metadata.currentTab = AppConstants.wldDomainSizing;
        currentProject.metadata.status = AppConstants.PROJECT_STATUS_INPROGRESS;
      }
      ctx.patchState({
        currentProject: currentProject,
        workloadDomains: updatedWlds
      });
    }
    return this.store.dispatch(new UpdateProject(ctx.getState().projectId, currentProject));
  }
  
  @Action(DeleteWorkloadDomain)
  deleteWorkloadDomain(ctx: StateContext<ProjectDetailsStateModel>, action: DeleteWorkloadDomain): Observable<any>{
    const projectInState = ctx.getState().currentProject;
    if (!projectInState || !projectInState.data || !projectInState.data.workloadDomains) {
      return of({ "error": "invalid state: no workload domains found" });
    }
    const currentProject: VCPM.Project = JSON.parse(JSON.stringify(projectInState));
    if (!currentProject.data.workloadDomains) {
      return of({ "error": "invalid state: no workload domains found" });
    }
    const tabIdIndex = currentProject.data.workloadDomains.findIndex((w: any) => w.id === action.tabId);
    if (tabIdIndex === -1) {
      return of({ "error": "invalid domain id" });
    }
    const reason = getSonDeletionMessage(currentProject.data, action.tabId);
    if (reason) return throwError(() => new Error(reason));
    currentProject.data.workloadDomains.splice(tabIdIndex, 1);
    collectOrphanedClustersAndOwners(currentProject.data);
    reconcileStorageStretch(currentProject, projectInState.data);
    syncManagementClusterType(currentProject.data);
    currentProject.data.output = undefined;
    if(currentProject) {
      currentProject.metadata.currentTab = AppConstants.wldDomainSizing;
      currentProject.metadata.status = AppConstants.PROJECT_STATUS_INPROGRESS;
    }
    const nextState: ProjectDetailsStateModel = {
      ...ctx.getState(),
      currentProject: currentProject,
      workloadDomains: currentProject.data.workloadDomains
    };
    return this.collaborationService.validateBeforeWrite(projectInState).pipe(
      switchMap(() => this.vcpmService.updateProject(nextState.projectId, nextState.currentProject ?? {})),
      throwIfEmpty(() => new Error('Project was not saved. Please retry.')),
      tap((response: any) => {
        if (String(ctx.getState().currentProject?.projectId) !== String(projectInState.projectId)) {
          throw new Error('Project changed before the save completed.');
        }
        const updatedState: ProjectDetailsStateModel = {
          ...nextState,
          currentProject: response
        };
        ctx.patchState({
          currentProject: updatedState.currentProject,
          workloadDomains: updatedState.currentProject?.data.workloadDomains,
          managementDomain: updatedState.currentProject?.data.managementDomain,
          summary: undefined
        });
      })
    );
  }

  @Action(CloneWorkloadDomain)
  cloneWorkloadDomain(ctx: StateContext<ProjectDetailsStateModel>, action: CloneWorkloadDomain){
    let project = JSON.parse(JSON.stringify(ctx.getState().currentProject));
    let projectData = JSON.parse(JSON.stringify(ctx.getState().currentProject?.data));
    let wlds: VCPM.WorkloadDomain[] = projectData.workloadDomains || [];
    if (!action.tabId) {
      return of({ "error": "invalid domain id" });
    }
    const wld = wlds.find((w) => w.id === action.tabId);
    if (!wld) {
      return of({ "error": "domain not found" });
    }
    const clonedDomainName = PCSStringUtils.generateUniqueCopyName(wld.domainName, wlds.map((w) => w.domainName));
    // Deep copy: a shallow spread would leave the clone sharing the source's workloadList and
    // clusterConfigurations (same cluster ids/names in both domains once serialised).
    const newWld: VCPM.WorkloadDomain = JSON.parse(JSON.stringify(wld));
    newWld.id = this.workloadDomainService.generateUniqueTwoDigitNumberId(wlds.map(wld => wld.id));
    newWld.domainName = clonedDomainName;
    // The clone gets its own cluster entities (new ids + counter names); workloads that use an
    // existing storage cluster keep pointing at it. Bumps projectData's next*ClusterNumber counters.
    remapClonedDomainClusters(newWld, projectData);
    wlds.splice(wlds.indexOf(wld) + 1, 0, newWld);
    projectData.workloadDomains = wlds;
    projectData.output = undefined;
    project.data = projectData;
    project.metadata.currentTab = AppConstants.wldDomainSizing;
    project.metadata.status = AppConstants.PROJECT_STATUS_INPROGRESS;
    // VCPM/currentProject invariant: keep currentProject in sync with the data being saved.
    ctx.patchState({
      workloadDomains: wlds,
      currentProject: JSON.parse(JSON.stringify(project))
    });
    return this.store.dispatch(new UpdateProject(ctx.getState().projectId, project));
  }


  @Action(UpdateManagementDomainRecommendation, { cancelUncompleted: true })
  updateManagementDomainRecommendation(ctx: StateContext<ProjectDetailsStateModel>, action: UpdateManagementDomainRecommendation){
    let project: VCPM.Project = JSON.parse(JSON.stringify(ctx.getState().currentProject));
    let projectData: VCPM.ProjectData = JSON.parse(JSON.stringify(ctx.getState().currentProject?.data));
    if(!projectData.output){
      projectData.output = {recommendation:{managementDomainRecommendation: {}}} as VCPM.SizingOutput;
    }
  if(!projectData.output.recommendation){
      projectData.output.recommendation = {managementDomainRecommendation: {}} as VCPM.Recommendation;
    }
    projectData.output.recommendation.managementDomainRecommendation = action.recommendation;
    // Clear summary to force recalculation when management domain is regenerated
    projectData.output.summary = undefined;
    project.data = projectData;
    if(action.userExit) {
      project.metadata.currentTab = AppConstants.mgntDomainSizing;
      project.metadata.status = AppConstants.PROJECT_STATUS_INPROGRESS;
    } else {
      project.metadata.currentTab = AppConstants.outputRecommendation;
      project.metadata.status = AppConstants.PROJECT_STATUS_COMPLETED;
    }
    ctx.patchState({
      currentProject: project,
      summary: undefined
    })
    return this.store.dispatch(new UpdateProject(ctx.getState().projectId, project, true));
  }

  @Action(UpdateWorkloadDomainRecommendation, { cancelUncompleted: true })
  updateWorkloadDomainRecommendation(ctx: StateContext<ProjectDetailsStateModel>, action: UpdateWorkloadDomainRecommendation){
    let project: VCPM.Project = JSON.parse(JSON.stringify(ctx.getState().currentProject));
    let projectData: VCPM.ProjectData = JSON.parse(JSON.stringify(ctx.getState().currentProject?.data));
    if(!projectData.output){
      projectData.output = {recommendation:{workloadDomainsRecommendation: {}}} as VCPM.SizingOutput;
    }
    if(!projectData.output.recommendation){
      projectData.output.recommendation = {workloadDomainsRecommendation: {}} as VCPM.Recommendation;
    }
    projectData.output.recommendation.workloadDomainsRecommendation = action.recommendation;
    projectData.output.recommendation.managementDomainRecommendation = undefined;
    projectData.output.summary = undefined;
    // Preserve managementDomain.selectedComponents so user selections (VCPM) are
    // available for reconciliation with new mgmtComponentsSuggestions on the
    // management domain sizing page (PCS-1742).
    project.data = projectData;
    if(action.userExit) {
      project.metadata.currentTab = AppConstants.wldDomainSizing;
      project.metadata.status = AppConstants.PROJECT_STATUS_INPROGRESS;
    } else {
      project.metadata.currentTab = AppConstants.mgntDomainSizing;
      project.metadata.status = AppConstants.PROJECT_STATUS_INPROGRESS;
    }
    ctx.patchState({
      currentProject: project,
      summary: undefined,
    })
    return this.store.dispatch(new UpdateProject(ctx.getState().projectId, project));
  }

  @Action(PPTReportDownload, { cancelUncompleted: true })
  getLiveOpticsFileConfig(
    ctx: StateContext<ProjectDetailsStateModel>,
    action: PPTReportDownload
  ) {
    return this.reportService.generateReport(action.product, action.version, action.project).pipe(
      tap((event: HttpEvent<Blob>) => {
        if (event.type === HttpEventType.DownloadProgress) {
          const progress = event.total ? Math.round((100 * event.loaded) / event.total) : 0 ;
          ctx.patchState({
            pptReportExportProgress: progress,
          });
        } else if (event.type === HttpEventType.Response) {
          if(event.body) {
            ctx.patchState({
              pptExportBlob: event.body,
            });
            // Track successful report download
            this.store.dispatch(new SaveSupercolliderEvent(
              action.project.projectId.toString(),
              SUPERCOLLIDER_EVENT_TYPE.REPORT_DOWNLOAD
            ));
          } else {
            ctx.patchState({
              pptExportBlob: undefined,
            });
          }
        }
      }),
      catchError((error) => {
        // Track failed report download
        this.store.dispatch(new SaveSupercolliderEvent(
          action.project.projectId.toString(),
          SUPERCOLLIDER_EVENT_TYPE.REPORT_DOWNLOAD_FAILURE
        ));
        throw error;
      })
    );
  }

  @Action(SaveSummary, { cancelUncompleted: true })
  saveSummary(ctx: StateContext<ProjectDetailsStateModel>, action: SaveSummary) {
    ctx.patchState({
      summary: action.summary
    });
    if(action.saveToVCPM) {
      let project: VCPM.Project = JSON.parse(JSON.stringify(ctx.getState().currentProject));
      if(project.data.output && action.summary) {
        project.data.output['summary'] = action.summary;
        return this.store.dispatch(new UpdateProject(ctx.getState().projectId, project));
      }
    }
    return EMPTY;
  }

  // No cancelUncompleted: every event must be sent.
  @Action(SaveSupercolliderEvent)
  saveSupercolliderEvent(ctx: StateContext<ProjectDetailsStateModel>, action: SaveSupercolliderEvent) {
    // Get userId from action, auth state, or the id token
    let userId = action.userId;
    if (!userId) {
      const user = this.store.selectSnapshot(AuthState.getLoggedInUserProfile);
      userId = user?.acct || user?.username || this.injector.get(AuthService).getUserEmail() || 'unknown-user';
    }

    return this.vcpmService.saveSupercolliderEvent(action.projectId, action.eventType, userId).pipe(
      catchError(() => EMPTY)
    );
  }

  @Action(ClearProjectDetailsState)
clearProjectDetailsState(ctx: StateContext<ProjectDetailsStateModel>) {
  ctx.patchState({
    projectId: '0',
    currentProject: undefined,
    sideNav: this.sideNavService.getInitState(),
    wldComponents: [],
    workloadInputConfiguration: {} as DataProvider.InputConfigurationResponse,
    mgmtDomainInputConfiguration: {},
    mgntComponents: {},
    hftRaidsConfig: {},
    ioProfileConfig: {},
    vsanOverheadsConfig: {},
    productConfig: {},
    productOffering: [],
    workloadDomains: [],
    managementDomain: {} as VCPM.ManagementDomain,
    recommendation: {},
    assumptions: [],
    managementDomainComponents: {
      model: undefined,
      dirty: false,
      status: '',
      errors: {}
    },
    vCenterHostCount: {
      hostCount: null,
      openModal: false
    },
    pptReportExportProgress: 0,
    pptExportBlob: undefined,
    summary: undefined,
    workloadTypeConfiguration: undefined,
    selectedRecommendationNode: 'summary' // Resets output tree navigation back to summary
  });
}
}

export interface NavTab {
  id: string;
  label: string;
  selected: boolean;
  panel: string;
  disabled: boolean;
}
export interface PCSTab {
  deployment?: string;
  architecture?: string;
  tabs: Array<NavTab>;
}

export interface VcenterHostCountModal {
  hostCount: number | null;
  openModal: boolean;
}